import type { TimekeepingBeforeEmploymentStartWarning } from 'src/utilities/time-balance/types';
import { t } from 'src/i18n';
import type { EmployeeWithTimekeepingPeriods } from 'src/store/selectors';
import { analyzeTimeBalance } from 'src/utilities/time-balance/time-balance';
import { LocalDate } from 'src/utilities/local-date';

function formatDate(date: Date): string {
  return LocalDate.fromString(date.toISOString().substring(0, 10)).toStringForDisplay();
}

function translate(key: string, options: Record<string, unknown>): string {
  return (t as any)(key, options) as string;
}

export function getTimekeepingOutsideEmploymentPeriodWarning(
  employeesInOrg: EmployeeWithTimekeepingPeriods[],
): TimekeepingBeforeEmploymentStartWarning {
  const analyses = employeesInOrg.map((employee) => ({
    uuid: employee.uuid,
    name: employee.name,
    analysis: analyzeTimeBalance(
      employee.periods,
      employee.forStaffing.employmentStartDate,
      employee.forStaffing.employmentEndDate,
    ),
  }));
  const affectedAnalyses = analyses.filter(({ analysis }) => analysis.hasTimekeepingOutsideEmploymentPeriod);
  const latestValidStartDate = affectedAnalyses.reduce<Date | undefined>((earliestDate, { analysis }) => {
    const date = analysis.latestValidStartDate;
    if (date === undefined) {
      return earliestDate;
    }
    if (earliestDate === undefined || date.getTime() < earliestDate.getTime()) {
      return date;
    }
    return earliestDate;
  }, undefined);
  const earliestValidEndDate = affectedAnalyses.reduce<Date | undefined>((latestDate, { analysis }) => {
    const date = analysis.earliestValidEndDate;
    if (date === undefined) {
      return latestDate;
    }
    if (latestDate === undefined || date.getTime() > latestDate.getTime()) {
      return date;
    }
    return latestDate;
  }, undefined);
  return {
    icon: '/images/alert.svg',
    headerText: t('employees.startDateWarning.header'),
    callToAction: t('employees.startDateWarning.callToAction'),
    employeeListIntro: t('employees.startDateWarning.employeeListIntro'),
    affectedEmployees: affectedAnalyses.map(({ uuid }) => uuid),
    affectedEmployeeNames: affectedAnalyses.map(({ uuid, name }) => ({ uuid, name })),
    latestValidStartDate,
    earliestValidEndDate,
    latestValidStartDateText:
      latestValidStartDate === undefined
        ? undefined
        : translate('employees.startDateWarning.latestValidStartDate', { date: formatDate(latestValidStartDate) }),
    earliestValidEndDateText:
      earliestValidEndDate === undefined
        ? undefined
        : translate('employees.startDateWarning.earliestValidEndDate', { date: formatDate(earliestValidEndDate) }),
  };
}
